import React, { useEffect, useRef, useState } from 'react';
import { LuCircleCheck, LuLoader, LuMail } from 'react-icons/lu';

import { registerEmail } from '../../shared/api/notifications';
import { auth } from '../../shared/firebase/firebase';

interface Props {
  isConnected: boolean;
  externalId?: string;
  linkedAt?: string;
  onRefresh: () => void;
  onDisconnect: () => Promise<void>;
}

// No manual email entry — this account's Firebase email is the only address a user can
// register (BE just needs to validate ownership via the click-through link), so we send it
// straight through and rely entirely on BE's register/verify pair for confirmation.
const EmailIntegration: React.FC<Props> = ({ isConnected, externalId, linkedAt, onRefresh, onDisconnect }) => {
  const accountEmail = auth.currentUser?.email ?? null;
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollCountRef.current = 0;
  };

  useEffect(() => {
    if (isConnected && waiting) {
      setWaiting(false);
      stopPolling();
    }
  }, [isConnected, waiting]);

  useEffect(() => () => stopPolling(), []);

  const handleConnect = async () => {
    if (!accountEmail) return;
    setLoading(true);
    setError(null);
    try {
      await registerEmail(accountEmail);
      setWaiting(true);
      pollCountRef.current = 0;
      pollRef.current = setInterval(() => {
        pollCountRef.current += 1;
        onRefresh();
        if (pollCountRef.current >= 12) {
          stopPolling();
          setWaiting(false);
        }
      }, 5000);
    } catch {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    stopPolling();
    setWaiting(false);
    onRefresh();
  };

  const handleCancel = () => {
    stopPolling();
    setWaiting(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await onDisconnect();
    } catch {
      setError('Failed to disconnect email. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  if (isConnected) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LuCircleCheck className="text-success text-xl shrink-0" />
            <div>
              <div className="text-sm font-medium">Connected</div>
              {externalId && <div className="text-xs opacity-60">{externalId}</div>}
              {linkedAt && <div className="text-xs opacity-60">Since {new Date(linkedAt).toLocaleDateString()}</div>}
            </div>
          </div>
          <button className="btn btn-sm btn-outline btn-error" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? <LuLoader className="animate-spin" /> : 'Disconnect'}
          </button>
        </div>
        {error && <div className="text-error text-sm">{error}</div>}
      </div>
    );
  }

  if (waiting) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm opacity-70">
          <LuLoader className="animate-spin shrink-0" />
          Check your inbox — click the link we sent to {accountEmail} to confirm.
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={handleDone}>
            Done
          </button>
          <button className="btn btn-sm btn-ghost" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && <div className="text-error text-sm">{error}</div>}
      <div className="text-sm opacity-70">
        Send notifications to your MFlow account email{accountEmail ? ` (${accountEmail})` : ''}. We&apos;ll send a
        verification link — click it to confirm.
      </div>
      <button className="btn btn-sm btn-primary gap-2" onClick={handleConnect} disabled={loading || !accountEmail}>
        <LuMail />
        {loading ? 'Sending…' : 'Verify email'}
      </button>
    </div>
  );
};

export default EmailIntegration;
